import { useContext, type ReactNode } from 'react';
import { useAuth } from 'react-oidc-context';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import {
  LENS_POOL_NAME,
  OPTICS_POOL_NAME,
  POWER_POOL_NAME,
  PULSE_POOL_NAME
} from 'solaris-lens-common-lib/src/constants/services';
import { getRightMenuExternalLinks } from 'solaris-lens-common-lib-web/src/utils/getRightMenuExternalLinks';

import styles from './styles.module.css';
import AdminToolIcon from '@/assets/icons/admintool-icon.svg?react';
import Arrow from '@/assets/icons/arrow.svg?react';
import ExternalIcon from '@/assets/icons/external.svg?react';
import ReportIcon from '@/assets/icons/report.svg?react';
// import BellIcon from '@/assets/icons/bell.svg?react';
// import LockIcon from '@/assets/icons/lock.svg?react';
import UserIcon from '@/assets/icons/user.svg?react';
// import UserIcon2 from '@/assets/icons/user2.svg?react';
import { APP_VERSION, POOL_NAME } from '@/constants';
import { PermissionsList } from '@/constants/permissionsList';
import DrawerContext from '@/context/DrawerContext/DrawerContext';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { signOutRedirect } from '@/oidcConfig';
import { clearOnLogOut } from '@/slices/authSlice';
import LensIcon from '@/solaris-lens-common-lib/src/assets/Lens-icon.svg?react';
import OpticsIcon from '@/solaris-lens-common-lib/src/assets/Optics-icon.svg?react';
import PulseIcon from '@/solaris-lens-common-lib/src/assets/Pulse-icon.svg?react';
import { RootState } from '@/store';

/** Option C: commons-lib SVG assets via Vite `?react` (CRA `getExternalLinkIcon` is not used). */
const EXTERNAL_LINK_ICONS: Record<string, ReactNode> = {
  [POWER_POOL_NAME]: <PulseIcon />,
  [PULSE_POOL_NAME]: <PulseIcon />,
  [LENS_POOL_NAME]: <LensIcon />,
  [OPTICS_POOL_NAME]: <OpticsIcon />
};

const NavigationMenu = () => {
  const drawerCtx = useContext(DrawerContext);
  const dispatch = useAppDispatch();
  const auth = useAuth();
  const authState = useSelector((state: RootState) => state.auth);
  const auditableElements = useSelector(
    (state: RootState) => state.auditableElements?.authorizations?.permissions
  ) as PermissionsList;
  const isOidcClient = authState.isOidcClient;
  const navigate = useNavigate();

  const userProfile = useSelector((state: RootState) => state.userProfile);
  const name =
    userProfile?.name ||
    userProfile?.customAtributes?.name ||
    userProfile?.username ||
    'Pulse User';
  const userEmail = userProfile?.email;
  const sourcePoolName = authState?.poolName || POOL_NAME;

  const menuItems = [
    {
      icon: <UserIcon />,
      text: 'Profile',
      onClick: () => navigate('/profile'),
      isAllowed: true,
      external: false
    },
    {
      icon: <AdminToolIcon />,
      text: 'Admin Tool',
      onClick: () => navigate('/admin-tool'),
      isAllowed: auditableElements?.['ADMIN_TOOL'],
      external: false
    },
    {
      icon: <ReportIcon />,
      text: 'Reporting',
      onClick: () => navigate('/audit'),
      isAllowed: auditableElements?.['REPORTING'],
      external: false
    }
  ];

  const externalLinks = getRightMenuExternalLinks({
    apps: userProfile?.apps,
    sourcePoolName,
    accessToken: authState.accessToken
  });

  const onLogout = () => {
    if (isOidcClient) {
      try {
        const idToken = auth?.user?.id_token;
        const email = auth?.user?.profile?.email;

        auth.signoutSilent({
          id_token_hint: idToken
        });
        dispatch(clearOnLogOut());
        localStorage.clear();
        signOutRedirect(idToken, email);
      } catch (error) {
        console.error('Error signing out:', error);
      }
      return;
    }

    dispatch(clearOnLogOut());
    window.location.href = '/login';
  };

  return (
    <div className={styles.drawerContent}>
      <div className={styles.drawerHeader}>
        <button
          id="navigationMenuCloseButton"
          className={styles.backButton}
          onClick={() => drawerCtx.closeDrawer()}
        >
          <Arrow className={styles.arrowIcon} width={20} height={20} />
        </button>
        <div className={styles.userInfo}>
          <h3 className={styles.userName}>{name}</h3>
          <p className={styles.userEmail}>{userEmail}</p>
        </div>
        <div className={styles.userAvatar}>
          <UserIcon />
        </div>
      </div>

      <div className={styles.menuOptions}>
        {menuItems.map(
          (item, index) =>
            item.isAllowed && (
              <button
                id="navigationMenuAdminTool"
                className={styles.menuItem}
                key={index}
                onClick={() => item.onClick()}
              >
                <div className={styles.menuItemIcon}>{item.icon}</div>
                <span className={styles.menuItemText}>{item.text}</span>
                {item?.external && (
                  <ExternalIcon className={styles.externalIcon} />
                )}
              </button>
            )
        )}

        {externalLinks.length > 0 && (
          <div className={styles.externalLinksSection}>
            <div className={styles.externalLinksHeader}>External links</div>
            {externalLinks.map(item => (
              <button
                id={item.id}
                className={styles.menuItem}
                key={item.id}
                onClick={() => item.onClick()}
              >
                <div className={styles.menuItemIcon}>
                  {EXTERNAL_LINK_ICONS[item.poolName]}
                </div>
                <span className={styles.menuItemText}>{item.text}</span>
                <ExternalIcon className={styles.externalIcon} />
              </button>
            ))}
          </div>
        )}
      </div>

      <div className={styles.drawerFooter}>
        <button
          id="navigationMenuLogoutButton"
          className={styles.logoutButton}
          onClick={onLogout}
        >
          LOGOUT
        </button>
        <div className={styles.versionInfo}>
          Solaris Power 2025 v{APP_VERSION}
        </div>
      </div>
    </div>
  );
};

export default NavigationMenu;
